// The browser half of the LMSR market maker, ported from src/features/market/lmsr.ts.
// The trade panel previews a fill locally before it POSTs, exactly as the Next component
// did, so the maths has to exist on both sides: `app/lib/lmsr.rb` is the server copy and
// the two must stay in step (the API re-prices every trade before it writes it).
// `_priceAfter` in the Next file is knip-dead and is not ported.

type QVector = Record<string, number>;

const SAFETY_MAX_SHARES = 10_000_000;

function shifted(q: QVector, b: number, ids: string[]) {
  const xs = ids.map((id) => (q[id] ?? 0) / b);
  const m = xs.length === 0 ? 0 : Math.max(...xs);
  return { xs, m };
}

function cost(q: QVector, b: number, ids: string[]): number {
  if (ids.length === 0) return 0;
  const { xs, m } = shifted(q, b, ids);
  const sumExp = xs.reduce((s, x) => s + Math.exp(x - m), 0);
  return b * (m + Math.log(sumExp));
}

export function prices(
  q: QVector,
  b: number,
  ids: string[],
): Record<string, number> {
  if (ids.length === 0) return {};
  const { xs, m } = shifted(q, b, ids);
  const exps = xs.map((x) => Math.exp(x - m));
  const sum = exps.reduce((s, e) => s + e, 0);
  const out: Record<string, number> = {};
  ids.forEach((id, i) => {
    out[id] = exps[i] / sum;
  });
  return out;
}

export function priceOf(
  q: QVector,
  b: number,
  ids: string[],
  projectId: string,
): number {
  return prices(q, b, ids)[projectId] ?? 0;
}

/**
 * Greatest integer s such that cost(q with q[k]+=s) - cost(q) <= bananasNet.
 * Returns the integer shares plus the rounded-down actual cost in bananas.
 */
export function sharesForBananas(
  q: QVector,
  b: number,
  ids: string[],
  projectId: string,
  bananasNet: number,
): { shares: number; actualCost: number } {
  if (bananasNet <= 0 || ids.length === 0) return { shares: 0, actualCost: 0 };
  const c0 = cost(q, b, ids);
  const cur = q[projectId] ?? 0;
  const deltaCost = (s: number) => {
    const qNew = { ...q, [projectId]: cur + s };
    return cost(qNew, b, ids) - c0;
  };

  let lo = 0;
  let hi = 1;
  while (deltaCost(hi) <= bananasNet) {
    lo = hi;
    hi *= 2;
    if (hi > SAFETY_MAX_SHARES) {
      hi = SAFETY_MAX_SHARES;
      break;
    }
  }
  while (lo + 1 < hi) {
    const mid = Math.floor((lo + hi) / 2);
    if (deltaCost(mid) <= bananasNet) lo = mid;
    else hi = mid;
  }
  const actualCost = Math.floor(deltaCost(lo));
  return { shares: lo, actualCost };
}

/** Bananas received for selling `shares` of project k (rounded down). */
export function bananasForSell(
  q: QVector,
  b: number,
  ids: string[],
  projectId: string,
  shares: number,
): number {
  if (shares <= 0 || ids.length === 0) return 0;
  const c0 = cost(q, b, ids);
  const cur = q[projectId] ?? 0;
  const qNew = { ...q, [projectId]: cur - shares };
  return Math.floor(c0 - cost(qNew, b, ids));
}

export function applyBuy(
  q: QVector,
  projectId: string,
  shares: number,
): QVector {
  return { ...q, [projectId]: (q[projectId] ?? 0) + shares };
}

export function applySell(
  q: QVector,
  projectId: string,
  shares: number,
): QVector {
  return { ...q, [projectId]: (q[projectId] ?? 0) - shares };
}
